<template>
  <b-container fluid class="all-assets-base-container">
    <b-row>
      <div class="col-12">
        <search
          isFromAllAssetsPage
          @findLicenseByChange="handleFindLicenseByChange"
          @searchInputChange="handleSearchInputChange"
          @orderDateChange="handleOrderDateChange"
          @licenseTypeChange="handleLicenseTypeChange"
          @fromDateChange="handleFromDateChange"
          @toDateChange="handleToDateChange"
          @advancedSearchButtonClick="handleAdvancedSearchButtonClick"
          @onSearchClick="handleSearchClick"
          @clearSearchClick="handleClearSearchClick"
          :isSearchEmpty="isSearchEmpty"
          v-bind="search"
          :isAdvancedSearchVisible="search.showAdvancedSearch"
        />
      </div>
    </b-row>
    <div v-if="!loading">
      <b-row v-if="list.length" class="all-licenses-table-row">
        <div class="col-12 col-md-4">
          <b-button
            variant="primary"
            size="lg"
            class="all-assets-cta-btn"
            @click="downloadCSV"
          >
            {{ $t('exportList') }}
            <span class="icon-download export-list-icon"></span>
          </b-button>
        </div>
        <div class="col-12 d-sm-block d-md-none options-btn-container">
          <a href="#" @click.stop.prevent="showOptions = !showOptions">
            {{ $t('viewHideOptions', { action: !showOptions ? $t('view') : $tc('hide', 1) } ) }}
          </a>
        </div>
        <div :class="['col-12 col-md-5 offset-md-3 options-container', { show: showOptions }]">
          <div class="row">
            <div class="col-sm-12 col-md page-size-col">
              <fieldset class="form-group">
                <legend>{{ $t('show') }}</legend>
                <div class="select-container">
                  <b-select
                    v-model="pageOption"
                    @change="handlePageNumberChange"
                    :options="pageOptions"
                  />
                </div>
              </fieldset>
            </div>
            <div class="col-sm-12 col-md">
              <fieldset class="form-group">
                <legend>{{ $t('dateRange') }}</legend>
                <div class="select-container">
                  <b-input
                    :value="dateRangeValue"
                    disabled
                  />
                </div>
              </fieldset>
            </div>
          </div>
        </div>
      </b-row>
      <b-row v-if="list.length">
        <div class="col-12">
          <table class="all-assets-table">
            <thead>
              <tr>
                <th></th>
                <th>{{ $t('productName') }}</th>
                <th>
                  <b-select
                    class="all-assets-dynamic-column-select"
                    v-model="dynamicColumnOption"
                    @change="handleDynamicColumnOptionChange"
                    :options="dynamicColumnOptions"
                  />
                </th>
                <th>{{ $t('quantity') }}</th>
              </tr>
            </thead>
            <tbody>
              <all-assets-list-item
                :key="item.id"
                v-for="(item, i) in list"
                :item="item"
                :isOdd="i % 2 !== 0"
                :dynamicKey="dynamicColumnOption"
              />
            </tbody>
          </table>
        </div>
      </b-row>
      <b-row v-if="list.length">
        <div class="col-12">
          <div class="pagination-container">
            <b-pagination
              hide-goto-end-buttons
              :value="currentPage"
              :limit="6"
              @change="handlePageChange"
              :per-page="pageOption"
              :prev-text="$t('paginationPrevText')"
              :next-text="$t('paginationNextText')"
              :total-rows="totalAssetsCount"
            />
          </div>
        </div>
      </b-row>
      <b-container fluid v-else>
        <b-row class="no-records-container">
          <h3>{{ $t('noRecordsFound') }}</h3>
        </b-row>
      </b-container>
    </div>
    <div v-else>
      <loading />
    </div>
  </b-container>
</template>

<script>
import Vue from 'vue'
import download from 'downloadjs'
import csv from 'papaparse'
import moment from 'moment'

import Search from './Search'
import AllAssetsListItem from './AllAssetsListItem'
import Loading from './Loading'
import SearchMixin from '../mixins/SearchMixin'
import DateFormatMixin from '../mixins/DateFormatMixin'

export default {
  name: 'AllAssets',
  components: {
    'search': Search,
    'all-assets-list-item': AllAssetsListItem,
    'loading': Loading
  },
  mixins: [SearchMixin, DateFormatMixin],
  mounted () {
    this.fetchAssets()
  },
  data () {
    return {
      showOptions: false,
      currentPage: 1,
      pageOption: 25,
      pageOptions: [25, 50],
      dateRangeValue: '',
      dynamicColumnOption: 'orderNumber',
      dynamicColumnOptions: [
        { value: 'orderNumber', text: this.$tc('orderNumber', 2) },
        { value: 'licenseAccessCode', text: this.$t('licenseAccessCode') }
      ],
      list: [],
      loading: false,
      totalAssetsCount: 0,
      wasSearchPerformed: false
    }
  },
  computed: {
    isSearchInputPresent () {
      return this.search.searchInput !== ''
    },
    searchBy () {
      return this.isSearchInputPresent ? this.search.findLicenseByOption : null
    },
    searchByValue () {
      return this.isSearchInputPresent ? this.search.searchInput.trim() : null
    },
    areFromAndToDatesPresent () {
      const { fromDateValue, toDateValue } = this.search
      return fromDateValue !== '' && toDateValue !== ''
    },
    advSearchBy () {
      if (this.areFromAndToDatesPresent) {
        if (this.search.dateOption === 'StartDate') return 'CreatedDate'
        else if (this.search.dateOption === 'ExpirationDate') return 'ExpiryDate'
        return null
      }
      return null
    },
    advDateFrom () {
      const { fromDateValue } = this.search
      return fromDateValue !== '' ? fromDateValue : null
    },
    advDateTo () {
      const { toDateValue } = this.search
      return toDateValue !== '' ? toDateValue : null
    },
    isSearchEmpty () {
      if (
        this.search.searchInput.length >= 2 ||
        (this.search.fromDateValue !== '' && this.search.toDateValue !== '')
      ) {
        return false
      }
      return true
    },
    requestBody () {
      return {
        customerID: this.$store.getters.person.customerId,
        noOfItems: this.pageOption,
        pageNumber: this.currentPage,
        searchBy: this.searchBy,
        searchByValue: this.searchByValue,
        advSearchBy: this.advSearchBy,
        advDateFrom: this.advDateFrom,
        advDateTo: this.advDateTo
      }
    }
  },
  methods: {
    getReadableDate (date) {
      if (!date) return 'N/A'
      if (+moment(date).format('YYYY').includes('1752')) return 'N/A'
      const temp = moment(date).format(this.getDateFormatBasedOnLocale())
      return (temp.includes('0001')) || (temp.includes('1752')) ? 'N/A' : temp
    },
    fetchAssets () {
      this.loading = true
      return this.bottle.post(this.UrlBuilder.allAssetsSearchUrl, this.requestBody)
        .then(({ data: { AssetList = [], TotalCount, MinDate, MaxDate } }) => {
          this.dateRangeValue = this.getReadableDate(MinDate) +
            ` ${this.$t('to')} ` +
            this.getReadableDate(MaxDate)
          this.totalAssetsCount = TotalCount
          this.list = AssetList.map(d => ({
            id: d.AssetID,
            productName: d.ProductName,
            orderNumber: d.SalesOrderNumber,
            licenseAccessCode: d.SerialNumber,
            quantity: d.Quantity,
            purchasedDate: d.CreatedDate,
            expirationDate: d.ExpiryDate,
            list: d.AssetComponentList.map(i => ({
              id: i.assetcomponentID,
              name: i.ProductName,
              laCode: i.SerialNumber,
              quantity: i.Quantity,
              lineItemNumber: `${d.SalesOrderNumber}/${i.LineItemNumber}`
            }))
          }))
        })
        .catch((error) => {
          this.loggly.log({
            errorMessage: 'Failed to fetch assets',
            errorObject: error,
            errorStack: error.stack
          })
        })
        .finally(() => {
          this.loading = false
        })
    },
    handleAdvancedSearchButtonClick (showAdvancedSearch) {
      this.search.showAdvancedSearch = showAdvancedSearch
    },
    handleSearchClick () {
      if (!this.isSearchEmpty) {
        this.currentPage = 1
        this.fetchAssets()
          .then(() => { this.wasSearchPerformed = true })
      }
    },
    handlePageNumberChange (pageNumber) {
      this.pageOption = pageNumber
      this.currentPage = 1
      this.fetchAssets()
    },
    handleDynamicColumnOptionChange (dynamicColumnOption) {
      this.dynamicColumnOption = dynamicColumnOption
    },
    handlePageChange (currentPage) {
      this.currentPage = currentPage
      if (!this.wasSearchPerformed) {
        this.search.searchInput = ''
        this.search.fromDateValue = ''
        this.search.toDateValue = ''
      }
      this.fetchAssets()
    },
    handleClearSearchClick () {
      this.search.searchInput = ''
      this.search.findLicenseByOption = 'SerialNumber'
      this.search.dateOption = 'StartDate'
      this.search.fromDateValue = ''
      this.search.toDateValue = ''
      this.showOptions = false
      this.currentPage = 1
      this.wasSearchPerformed = false
      this.fetchAssets()
    },
    getOrderLineItemNumber (orderNumber, lineItemNumber) {
      if (!lineItemNumber) return 'N/A'
      else return `${orderNumber}/${lineItemNumber}`
    },
    getExpiryDate (date) {
      return date === 'EndDate' ? 'ExpiryDate' : date
    },
    cleanData (dataToClean) {
      var checkRegex = '^[=+\\-@].*'
      return dataToClean.length > 0 ? (String(dataToClean).match(checkRegex) !== null ? '\'' + dataToClean : dataToClean) : dataToClean
    },
    downloadCSV () {
      this.bottle.post(this.UrlBuilder.allAssetsExportUrl, { ...this.requestBody })
          .then(({ data }) => {
            Vue.AppInsights.trackEvent('Download action', { ...this.requestBody })
            try {
              data = csv.parse(data, {
                skipEmptyLines: true
              }).data
              data = data
                .map(([sNo, , , , , qty, oNo, , pName, , lNo, , eDate]) =>
                  ([this.cleanData(pName), this.cleanData(sNo), this.cleanData(this.getOrderLineItemNumber(oNo, lNo)), this.cleanData(qty), this.cleanData(this.getExpiryDate(eDate))])
                )
              data = csv.unparse(data)
            } catch (error) {
              this.loggly.log({
                errorMessage: 'Error parsing the CSV data',
                errorObject: error,
                errorStack: error.stack
              })
            }
            download(data, 'CitrixProductList.csv', 'application/octet-stream')
          })
          .catch((error) => {
            this.loggly.log({
              errorMessage: 'Failed to fetch csv file for download',
              errorObject: error,
              errorStack: error.stack
            })
          })
    }
  }
}
</script>

<style lang="scss">
  @import '../styles/variables.scss';

  fieldset {
    border: 1px solid $lightGray;
    border-radius: 5px;
    padding: 12px 0 0;
    width: 100%;
  }
  fieldset > legend {
    padding: 0 10px;
    margin-left: 10px;
    width: auto;
    font-size: 0.75rem;
    color: $darkGray;
    margin-bottom: 0;
    z-index: $legendZIndex;
    position: relative;
    background-color: white;
  }
  .select-container {
    margin: -20px 0 0;
  }
  select {
    border: none !important;
    height: 30px;
    width: 100% !important;
    text-indent: 10px;
  }
  select {
    font-size: 1rem;
  }
  select:hover,
  select:focus,
  select:active {
    outline: 0 !important;
    border: none !important;
    box-shadow: none !important;
  }
  .options-btn-container {
    padding: 15px;
    text-align: center;
  }
  .options-container {
    min-height: 0;
    height: 0;
    opacity: 0;
    transition: height 0.5s;
  }
  .options-container.show {
    height: auto;
    opacity: 1;
    transition: height 0.5s;
  }
  .select-container > input.form-control:disabled {
    background-color: white;
    cursor: not-allowed;
    border: none;
    text-indent: 0;
  }
  .all-assets-dynamic-column-select,
  .all-assets-dynamic-column-select:active,
  .all-assets-dynamic-column-select:hover,
  .all-assets-dynamic-column-select:focus {
    background: $lightGray;
    border: 1px solid #babab8 !important;
    box-shadow: 2px 2px #babab8, 2px 2px #babab8;
    margin-top: -15px;
    margin-bottom: -15px;
    margin-left: -15px;
    text-indent: 0;
    -webkit-appearance: menulist;
  }
  .all-assets-base-container {
    padding-left: 0;
    padding-right: 0;
    padding-bottom: 50px;
  }
  .all-assets-cta-btn {
    width: 100%;
    margin-top: 10px;
    height: 40px;
    background: $primary;
    line-height: 20px;
    font-size: 1rem;
    border-radius: 5px !important;
  }
  .all-assets-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0 0.6rem;
    z-index: -2;
  }
  .all-assets-table thead tr > th:first-child {
    width: 5%;
  }
  .all-assets-table thead tr > th:nth-child(2) {
    width: 55%;
    border-right: 1px solid #dee2e6;
    border-top-right-radius: 5px;
    border-bottom-right-radius: 5px;
  }
  .all-assets-table thead tr > th:nth-child(3) {
    width: 30%;
    display: none;
  }
  .all-assets-table thead tr > th:last-child {
    width: 10%;
    text-align: right;
    display: none;
  }
  .all-assets-table thead,
  .all-assets-table thead > tr {
    min-height: 50px;
  }
  .all-assets-table th {
    background: $lightGray;
    border-color: $lightGray;
    font-weight: 400;
    padding: 15px 12px;
  }
  .all-assets-table th:first-child,
  .all-assets-table td:first-child {
    border-left: 1px solid #dee2e6;
    border-top-left-radius: 5px;
    border-bottom-left-radius: 5px;
  }
  .all-assets-table th:last-child,
  .all-assets-table td:last-child {
    border-right: 1px solid #dee2e6;
    border-top-right-radius: 5px;
    border-bottom-right-radius: 5px;
  }
  .all-assets-table th,
  .all-assets-table td {
    border-top: 1px solid #dee2e6;
    border-bottom: 1px solid #dee2e6;
  }
  .export-list-icon {
    vertical-align: middle;
    font-size: 20px;
    margin-left: 5px;
  }
  .no-records-container {
    background-color: $lightGray;
    padding: 20px;
    margin-top: 20px;
    margin-bottom: 20px;
  }
  .no-records-container h3 {
    font-weight: 400;
  }
  .pagination-container {
    display: flex;
    justify-content: flex-end;
    margin-top: 20px;
    margin-bottom: 20px;
  }

  @media (min-width: 768px) {
    .options-container {
      height: auto;
      opacity: 1;
    }
    .page-size-col {
      max-width: 120px;
      flex: 0 0 120px;
    }
    .all-assets-table thead tr > th:nth-child(2) {
      border-right: none;
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    }
    .all-assets-table thead tr > th:nth-child(3) {
      display: table-cell;
    }
    .all-assets-table thead tr > th:last-child {
      display: table-cell;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/AllAssets.vue